import { RoutePath } from '@/enums/Routes';
import ClientOAuth2 from 'client-oauth2';
import { includes, get } from 'lodash';
import { computed, reactive } from 'vue';
import { RouteLocationNormalized } from 'vue-router';
import { SalesforceState, SalesforceComputedState } from './types';
import { getIntegration, getConfig } from '@/services/config';
import { useCookies } from 'vue3-cookies';
import { UserPreferenceCookies } from '../../../enums/Cookies';

let client: ClientOAuth2 | null = null;

const state: SalesforceState = reactive({
  accessToken: '',
  domain: '',
});

export const salesforceState: SalesforceComputedState = {
  accessToken: computed(() => state.accessToken),
  domain: computed(() => state.domain),
};

const getClient = () => {
  if (client === null) {
    const salesforceIntegration = getIntegration('salesforceNameResolution');
    const clientId = salesforceIntegration.clientId;
    state.domain = salesforceIntegration.domain;

    const authorizationUri = state.domain + '/services/oauth2/authorize';
    client = new ClientOAuth2({
      clientId,
      authorizationUri,
      redirectUri: window.location.origin + '/salesforce',
    });
  }

  return client;
};

const isCallBackRoute = () => {
  return includes(window.location.hash, 'salesforce.com');
};

// handles authenticating to salesforce and call back
export const routeGuard = (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: Function
) => {
  if (isCallBackRoute()) {
    let returnUrl = localStorage.getItem('returnUrl');
    if (
      returnUrl === RoutePath.SALESFORCE_CALLBACK ||
      returnUrl === RoutePath.ERROR ||
      returnUrl === null
    ) {
      returnUrl = RoutePath.ROOT;
    }

    return getClient()
      .token.getToken(window.location)
      .then(user => {
        state.accessToken = user.accessToken;
        useCookies().cookies.set(
          UserPreferenceCookies.SALESFORCE_CLIENT_NAME_RESOLVER,
          'enabled',
          '1y'
        );
        next(returnUrl);
      });
  } else {
    next(RoutePath.ROOT);
  }
};

// Checks if salesforce is authenticated, if not redirect to salesforce page
export const authenticateWithSalesforce = () => {
  if (!includes([RoutePath.LOGGED_OUT], window.location.pathname)) {
    if (!state.accessToken) {
      localStorage.setItem(
        'returnUrl',
        window.location.pathname + window.location.search
      );
      window.location.href = getClient().token.getUri();
    }
  }
};

class NameService {
  computedState: SalesforceComputedState;

  constructor() {
    this.computedState = salesforceState;
  }

  resolve = async (internalId: string) => {
    const accessToken = salesforceState.accessToken.value;
    if (!accessToken) {
      return;
    }

    let filterId = internalId;

    const precedingZeros = getConfig('precedingZeros');

    if (precedingZeros) {
      const idLength = internalId.length;
      if (idLength < 7) {
        let precedingChars = '';
        for (let index = 0; index < 7 - idLength; index++) {
          precedingChars += '0';
        }
        filterId = precedingChars + internalId;
      }
    }

    const headers = {
      Authorization: `Bearer ${accessToken}`,
    };

    return fetch(
      `${this.computedState.domain.value}/services/data/v52.0/query?` +
        new URLSearchParams({
          q: `SELECT Name from Account WHERE N_INTV__c = '${filterId}' AND RecordType.DeveloperName='Account'`,
        }),
      { headers: headers }
    )
      .then(response => response.json())
      .then(data => {
        const records = get(data, 'records', []);

        if (records.length === 0) {
          console.info(`Search resulted no results for ${filterId}`); // eslint-disable-line no-console
          return;
        }

        return get(records[0], 'Name', '');
      })
      .catch(error => {
        console.error(error); // eslint-disable-line no-console
        throw Error(`error calling the name resolution API`);
      });
  };
}

export default new NameService();
